import { z } from "zod";
import { useRootLoaderData } from "@made-people/gears-remix";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";
import { getMultipleStoriesFromGlobalStoryblokStoriesByComponentType } from "../utils/global-story-functions";

const deliveryTimeSettingsSchema = z.object({
  countries: z.array(z.string()),
  deliveryTimePrio1: z.string().optional(),
  deliveryTimePrio2: z.string().optional(),
  deliveryTimePrio3: z.string().optional(),
  deliveryTimePrio4: z.string().optional(),
  deliveryTimeMixed: z.string().optional(),
  expressEnabledLit: z.boolean().optional(),
  expressEnabledLogent: z.boolean().optional(),
});

export type DeliveryTimeSettings = z.infer<typeof deliveryTimeSettingsSchema>;

export const useDeliveryTimeSettings = (): DeliveryTimeSettings | null => {
  const rootLoaderData = useRootLoaderData();
  const { currentCountryCode } = useCountriesAndLanguages();

  if (!currentCountryCode) return null;

  const stories = getMultipleStoriesFromGlobalStoryblokStoriesByComponentType(
    "DeliveryTimeSettings",
    rootLoaderData?.settingsAndContentResponse?.globalStoryblokStories,
  );

  const match = stories.find(
    (story) =>
      Array.isArray(story?.countries) &&
      story.countries.includes(currentCountryCode),
  );

  if (!match) return null;

  const parsed = deliveryTimeSettingsSchema.safeParse(match);
  return parsed.success ? parsed.data : null;
};

export function getDeliveryTimeLabelFromSettings(
  settings: DeliveryTimeSettings | null,
  deliveryPriority: { priority: number; warehouseName: string } | "mixed",
): string | undefined {
  if (!settings) return undefined;

  if (deliveryPriority === "mixed") {
    return settings.deliveryTimeMixed;
  }

  const fieldMap: Record<number, string | undefined> = {
    1: settings.deliveryTimePrio1,
    2: settings.deliveryTimePrio2,
    3: settings.deliveryTimePrio3,
    4: settings.deliveryTimePrio4,
  };

  return fieldMap[deliveryPriority.priority];
}
